/**
 * The bed rule, pure (batch-29, item R1; plan run/vibe/batch-29/plan.md §4). Whole numbers only: no fraction ever
 * touches a fit test or money. The database gets the same rule as `bed_valid` / `bed_default` (item R2), and both are
 * run against the hand-worked vectors in tests/fixtures/bed-vectors.json.
 *
 * A room type knows its beds; a room knows who is in it; the rule says how many cots and cribs that takes.
 * One bed per person unless the event lets two people use a bed for two — and never three in a bed.
 */

/** A room type's numbers. `beds === null` means "not set": the type takes any party and nothing is added (today's behaviour). */
export interface RoomBeds {
  /** B — beds; null when the organizer has not set this type yet */
  beds: number | null;
  /** T — beds for two (T ≤ B) */
  bedsForTwo: number;
  /** P — the most people the room may hold */
  maxPeople: number;
  /** C — cot spaces */
  maxCots: number;
  /** R — crib spaces */
  maxCribs: number;
}

/** What the hotel and the event add to a room type's numbers. */
export interface BedSettings {
  /** ρ100 — cribs per unused cot space × 100 (200 = two cribs in the place of one cot) */
  cribsPerCot100: number;
  /** bb — babies in cribs count toward the people cap (the room type's own setting wins when it has one) */
  babiesCount: boolean;
  /** two people may use a bed for two (the event's setting; the word for it never reaches a guest screen) */
  twoPerBed: boolean;
}

/**
 * The event's "Two people may use one bed" setting as every screen reads it (R25: off everywhere unless an organizer
 * turns it on). Only a stored `true` is on — a value that is missing, null or anything else is off, the same as
 * `bed_valid` in the database (COALESCE(share_beds, false)).
 */
export const twoPerBedOf = (value: unknown): boolean => value === true;

/** Who is in the room: `others` are the people who are not in the crib group, `babies` the people who are. */
export interface RoomPeople { others: number; babies: number }
/** A room's extras: k cots and c cribs. */
export interface BedState { cots: number; cribs: number }

const whole = (n: number): boolean => Number.isInteger(n) && n >= 0;
export const isSet = (t: RoomBeds): boolean => t.beds !== null;

/** V(a, i, k, c): the seven tests of plan §4.3. A type that is not set is valid only with no cots and no cribs. */
export function bedValid(t: RoomBeds, s: BedSettings, p: RoomPeople, st: BedState): boolean {
  const { others: a, babies: i } = p;
  const { cots: k, cribs: c } = st;
  if (![a, i, k, c].every(whole)) return false;
  if (a < 1) return false;                                                    // V0 a room needs one person who is not a baby
  if (t.beds === null) return k === 0 && c === 0;
  const B = t.beds;
  if (c > i || k > t.maxCots) return false;                                   // V1
  const sleepers = a + (i - c);                                               // V2 everyone not in a crib needs a bed place or a cot
  if (sleepers + (s.babiesCount ? c : 0) > t.maxPeople) return false;         // V3 the people cap
  if (c > t.maxCribs + Math.floor((s.cribsPerCot100 * (t.maxCots - k)) / 100)) return false; // V4 rounded down once, on the total
  const places = s.twoPerBed ? B + t.bedsForTwo : B;                          // a bed holds one; a bed for two holds two only when allowed
  if (sleepers > k + places) return false;                                    // V5 never three in a bed
  if (k > Math.max(0, sleepers - B)) return false;                            // V6 no cot beyond what one-per-bed needs
  return true;
}

/** The cots a number of cribs gets: the most the rule allows, one person per bed first. Null when no cot count works. */
function cotsFor(t: RoomBeds, s: BedSettings, p: RoomPeople, cribs: number): BedState | null {
  if (t.beds === null) return null;
  const sleepers = p.others + p.babies - cribs;
  for (let k = Math.min(t.maxCots, Math.max(0, sleepers - t.beds)); k >= 0; k--) {
    if (bedValid(t, s, p, { cots: k, cribs })) return { cots: k, cribs };
  }
  return null;
}

/** D(a, i): what the system adds — a crib for every baby first, then cots for the overflow. Null = this room cannot take these people. */
export function bedDefault(t: RoomBeds, s: BedSettings, p: RoomPeople): BedState | null {
  if (!whole(p.others) || !whole(p.babies) || p.others < 1) return null;
  if (t.beds === null) return { cots: 0, cribs: 0 };
  for (let c = p.babies; c >= 0; c--) {
    const st = cotsFor(t, s, p, c);
    if (st) return st;
  }
  return null;
}

/** A room fits a group exactly when the default exists. */
export const fits = (t: RoomBeds, s: BedSettings, p: RoomPeople): boolean => bedDefault(t, s, p) !== null;

/** "Remove cot" is offered only when the room is still valid with one cot fewer. */
export const canRemoveCot = (t: RoomBeds, s: BedSettings, p: RoomPeople, st: BedState): boolean =>
  st.cots > 0 && bedValid(t, s, p, { cots: st.cots - 1, cribs: st.cribs });

/** Removing a crib works the cots out again (a cot may take the crib's place). Null = not offered. */
export const removeCrib = (t: RoomBeds, s: BedSettings, p: RoomPeople, st: BedState): BedState | null =>
  st.cribs > 0 ? cotsFor(t, s, p, st.cribs - 1) : null;

/** Putting a removed cot back: one more cot, only when that is valid. */
export const addCot = (t: RoomBeds, s: BedSettings, p: RoomPeople, st: BedState): BedState | null => {
  const next = { cots: st.cots + 1, cribs: st.cribs };
  return bedValid(t, s, p, next) ? next : null;
};

/** Putting a removed crib back: one more crib, the cots worked out again. */
export const addCrib = (t: RoomBeds, s: BedSettings, p: RoomPeople, st: BedState): BedState | null =>
  st.cribs < p.babies ? cotsFor(t, s, p, st.cribs + 1) : null;

/** A room's cot and crib charge for the stay, in cents. A price of 0 is still a line on the page ("Free"). */
export function bedCharge(st: BedState, prices: { cotCents: number; cribCents: number }, nights: number): { cotCents: number; cribCents: number } {
  return { cotCents: st.cots * prices.cotCents * nights, cribCents: st.cribs * prices.cribCents * nights };
}
